import React, { useState, useContext, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { getAssociatedGroupedLineItems } from '@printdeal/business-logic';
import { DescriptionList } from '@printdeal/ui-library';
import { ParsedShippingAddressPerLineItem } from '@printdeal/checkout-frontend';
import OrderDetailContext from '../../contexts/OrderDetailContext';
import { SanitizedLineItem } from '../../../../../types/order/Order';
import canChangeShippingAddressHelper from '../LineItemActions/ChangeShippingAddress/CanChangeShippingAddressHelper';
import ChangeShippingAddressFeedbackModal
  from '../LineItemActions/ChangeShippingAddress/ChangeShippingAddressFeedbackModal';
import DeliveriesLink from './DeliveriesLink';
import { dispatchAccountEvent } from '../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';
import { formatPostalCode } from '../../../helpers/order-helper';
import ShippingAddressOnOrderLineItem from './new/ShippingAddressOnOrderLineItem';

const FeedbackOptions = {
  NONE: 'none',
  ERROR: 'error',
  WARNING: 'warning',
  SUCCESS: 'success',
};

interface ShippingAddressProps {
  lineItem: SanitizedLineItem;
  address: ParsedShippingAddressPerLineItem;
  setPendingChange: (pendingChange: boolean) => void;
  isMainAddress?: boolean;
}

const ShippingAddress = ({
  lineItem,
  address,
  setPendingChange,
  isMainAddress = false,
}: ShippingAddressProps) => {
  // Load the order data from the context
  const { order, reFetchOrder } = useContext(OrderDetailContext);

  // Abort if no order data is available yet
  if (!order || !reFetchOrder) {
    return null;
  }

  const {
    custom: { fields: { shippingAddressPerLineItem } },
    sane: { postalCodeDeliveries, unknownDeliveries },
  } = lineItem;
  const postalCodeLinks = postalCodeDeliveries[formatPostalCode(address.postalCode)] || [];
  const trackingLinks = shippingAddressPerLineItem.length > 1
    ? postalCodeLinks
    : [...postalCodeLinks, ...unknownDeliveries];

  const groupedLineItems = useMemo(
    () => ([
      lineItem,
      ...getAssociatedGroupedLineItems(order, lineItem, { reviewGroup: true }),
    ]),
    [lineItem, order],
  );

  const [feedbackStatus, setFeedbackStatus] = useState(FeedbackOptions.NONE);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(feedbackStatus !== FeedbackOptions.NONE);
  const isShippingAddressEditable = canChangeShippingAddressHelper(lineItem);

  const handleCloseFeedbackModal = (eventName: string) => {
    setIsFeedbackModalOpen(false);
    dispatchAccountEvent('order_detail_interaction', eventName);
  };

  return (
    <>
      <ShippingAddressOnOrderLineItem
        orderId={order.id}
        lineItem={lineItem as any}
        groupedLineItems={groupedLineItems as any[]}
        shippingAddress={address}
        canChangeShippingAddress={isShippingAddressEditable.enabled && isMainAddress}
        onChangeSuccess={async (responseData) => {
          await reFetchOrder({ reFetchPaymentMethods: true });

          if (responseData.some(({ status }) => status === 'failed')) {
            setFeedbackStatus(FeedbackOptions.ERROR);
          } else if (responseData.some(({ status }) => status === 'pending')) {
            setFeedbackStatus(FeedbackOptions.WARNING);
            setPendingChange(true);
          } else {
            setFeedbackStatus(FeedbackOptions.SUCCESS);
          }

          setIsFeedbackModalOpen(true);
          dispatchAccountEvent('order_detail_interaction', 'edit_shipping_address');
        }}
      >
        {({ shippingAddress, changeShippingAddressButton }) => (
          <DescriptionList className="md:pd-grid-cols-3">
            <DescriptionList.Title>
              <div className="pd-mb-2">
                {isMainAddress ? (
                  <FormattedMessage
                    id="account.order.addresses.delivery.title"
                    defaultMessage="Shipping address"
                  />
                ) : (
                  <FormattedMessage
                    id="splitShipmentAddress.list.extraDeliveryAddress.labelOnly"
                    defaultMessage="Extra delivery address"
                  />
                )}
                :
              </div>
              {changeShippingAddressButton}
            </DescriptionList.Title>
            <DescriptionList.Content
              className="md:pd-col-span-2"
            >
              {shippingAddress}
              {trackingLinks.map((link: string) => (
                <div key={link}>
                  <DeliveriesLink trackingLink={link} />
                </div>
              ))}
            </DescriptionList.Content>
          </DescriptionList>
        )}
      </ShippingAddressOnOrderLineItem>
      {
        isFeedbackModalOpen && (
          <ChangeShippingAddressFeedbackModal
            feedbackStatus={feedbackStatus as 'error' | 'warning' | 'success'}
            handleCloseModal={handleCloseFeedbackModal}
          />
        )
      }
    </>
  );
};

export default ShippingAddress;
